import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../store";
import { useMemo, useState } from "react";
import {
  Box,
  Button,
  ClickAwayListener,
  IconButton,
  MenuItem,
  MenuList,
  Paper,
  Tooltip,
} from "@mui/material";
import classes from "../Filter.module.scss";
import DetectMobileHelper from "../../../helpers/detect-mobile.helper";
import {
  Info as InfoIcon,
  ArrowDropDown as ArrowDropDownIcon,
  ArrowDropUp as ArrowDropUpIcon,
} from "@mui/icons-material/";
import { setTipContentLength } from "../../../../store/filters/tipFilters.slice";
import { ContentLengthOptions } from "../../../enums/ContentLengthOptions";
import { TIP_LENGTH_THRESHOLD } from "../../tip-length/TipLength";

const LENGTH_OPTIONS = [
  { value: ContentLengthOptions.ALL, label: "All Tips" },
  {
    value: ContentLengthOptions.SHORT,
    label: "Short Length",
    tooltip: `Less than ${
      TIP_LENGTH_THRESHOLD[ContentLengthOptions.MEDIUM]
    } words`,
  },
  {
    value: ContentLengthOptions.MEDIUM,
    label: "Medium Length",
    tooltip: `Between ${
      TIP_LENGTH_THRESHOLD[ContentLengthOptions.MEDIUM]
    } and ${TIP_LENGTH_THRESHOLD[ContentLengthOptions.LONG]} words`,
  },
  {
    value: ContentLengthOptions.LONG,
    label: "Long Length",
    tooltip: `More than ${
      TIP_LENGTH_THRESHOLD[ContentLengthOptions.LONG]
    } words`,
  },
];

const ContentLengthFilter = () => {
  const dispatch = useDispatch();
  const contentLength = useSelector(
    (state: RootState) =>
      state.tipFilters.tipFilters.contentLength ?? ContentLengthOptions.LONG
  );
  const [isDropdownOpened, setIsDropdownOpened] = useState<boolean>(false);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setIsDropdownOpened(!isDropdownOpened);
  };

  const handleClose = () => {
    setIsDropdownOpened(false);
  };

  const renderMenu = useMemo(() => {
    const handleOptionSelect = (value: ContentLengthOptions) => {
      dispatch(setTipContentLength(value));
      setIsDropdownOpened(false);
    };

    return (
      <Paper className={`${classes["search"]} mt-2`} elevation={3}>
        <MenuList className={`${classes["listbox-height"]} scrollbar`}>
          {LENGTH_OPTIONS.map((option) => (
            <MenuItem
              key={option.value}
              onClick={() => handleOptionSelect(option.value)}
              selected={contentLength === option.value}
              className={classes["option-color"]}
            >
              <Box className="d-flex align-items-center justify-content-between w-100">
                {option.label}
                {option.tooltip && (
                  <Tooltip title={option.tooltip} placement="left">
                    <IconButton
                      className="p-1"
                      size="small"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <InfoIcon fontSize="small" color="info" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </MenuItem>
          ))}
        </MenuList>
      </Paper>
    );
  }, [dispatch, contentLength]);

  const getFiltersLabel = useMemo(() => {
    const selectedOption = LENGTH_OPTIONS.find(
      (option) => option.value === contentLength
    );

    return selectedOption ? selectedOption.label : "Tip Length";
  }, [contentLength]);

  return (
    <Box>
      <Button
        onClick={handleClick}
        className={`button-text filter-button ${
          isDropdownOpened ? classes.selectedFilterButton : classes.filterButton
        }`}
        endIcon={isDropdownOpened ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
      >
        {getFiltersLabel}
      </Button>
      <Box>
        {isDropdownOpened &&
          (DetectMobileHelper.isMobile() ? (
            <>{renderMenu}</>
          ) : (
            <ClickAwayListener onClickAway={handleClose}>
              {renderMenu}
            </ClickAwayListener>
          ))}
      </Box>
    </Box>
  );
};

export default ContentLengthFilter;
